<template>
    <div class="evSavingsGraph__wrapper">
        <div class="evSavingsGraph__header">
            <div class="evSavingsGraph__titleAndIntro">
                <h2 class="evSavingsGraph__title">
                    Electric Car cost savings
                </h2>

                <div class="evSavingsGraph__intro">
                    Take a look at how the total cost of ownership of a plug-in car compares to that of a comparable petrol/diesel car
                </div>
            </div>

            <div class="evSavingsGraph__controls">
                <div class="evSavingsGraph__controlsText">
                    Savings are based on
                </div>

                <input type="number"
                       v-model="settings.milesPerYear"
                       @input="updateChart()"
                       step="1000"
                       min="0"
                       class="evSavingsGraph__mileageInput"
                       >

                <div class="evSavingsGraph__controlsText">
                    miles per year for {{ totalYears }} years
                </div>
            </div>
        </div>

        <div class="evSavingsGraph__legend">
            <div class="evSavingsGraph__legendItemWrap">
                <div class="evSavingsGraph__legendItem evSavingsGraph__legendItem--left">
                    <div class="evSavingsGraph__legendItemHeader evSavingsGraph__legendItemHeader--left">
                        <div class="evSavingsGraph__legendItemTitle evSavingsGraph__legendItemTitle--left">
                            <span class="evSavingsGraph__keyLine" :style="'background: ' + electricDataset.borderColor + ';' "></span>

                            {{ electricDataset.label }}
                        </div>

                        <div class="evSavingsGraph__legendItemCosts">
                            Total {{ totalYears }} year fuel costs: <strong>{{ electricCostFormatted }}</strong>
                        </div>
                    </div>

                    <div class="evSavingsGraph__legendItemFuelType evSavingsGraph__legendItemFuelType--electric">
                        {{ electricDataset.fuelType }}
                    </div>
                </div>

                <div class="evSavingsGraph__legendSavings">
                    Total fuel cost savings <strong>{{ totalSavingsFormatted }}</strong>
                </div>
            </div>

            <div class="evSavingsGraph__vs">VS</div>

            <div class="evSavingsGraph__legendItemWrap">
                <div class="evSavingsGraph__legendItem evSavingsGraph__legendItem--right">
                    <div class="evSavingsGraph__legendItemFuelType evSavingsGraph__legendItemFuelType--fossil">
                        {{ fossilDataset.fuelType }}
                    </div>

                    <div class="evSavingsGraph__legendItemHeader evSavingsGraph__legendItemHeader--right">
                        <div class="evSavingsGraph__legendItemTitle evSavingsGraph__legendItemTitle--right">
                            {{ fossilDataset.label }}

                            <span class="evSavingsGraph__keyLine" :style="'background: ' + fossilDataset.borderColor + ';' "></span>
                        </div>

                        <div class="evSavingsGraph__legendItemCosts">
                            Total {{ totalYears }} year fuel costs: <strong>{{ fossilCostFormatted }}</strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="evSavingsGraph__disclaimer" v-if="chartData.datasets.length > 1">
            * estimated savings over {{ totalYears }} years based on avg fuel/electricity prices and mileage - excluded tax costs/savings
        </div>

        <div class="evSavingsGraph__graphWrap">

            <div class="evSavingsGraph__graph">
                <canvas id="evChart"></canvas>
            </div>
        </div>
    </div>
</template>

<script>
    import {Chart, LineController, LineElement, Tooltip, CategoryScale, PointElement, LinearScale, Title} from 'chart.js'
    Chart.register(LineController, CategoryScale, Tooltip, LineElement, PointElement, LinearScale, Title);
    window['Chart'] = Chart;

    export default {
        name: "ev_savings_graph",

        props: {
            evGraphVehicles: {
                type: [Array, Object],
                default: []
            },
            defaultMilesPerYear: {
                type: [Number],
                default: 12000
            },
            monthsToSpan: {
                type: [Number],
                default: 60
            },
        },

        data () {
            return {
                chartData: {
                    labels: [],
                    datasets: []
                },
                electricDataset: null,
                fossilDataset: null,
                totalSavings: 0,
                totalYears: 0,
                chart: null,
                ctx: null,
                settings: {
                    milesPerYear: 12000, //User defined miles per year
                    totalMonths: 60, //Total amount of months the data will span
                    includePurchasePrice: false, // Include the purchase price in cost comparisons
                },
                updateDelay: 200,
                updateTimeout: null,
                costSavings: [],
                electricCost: 0,
                fossilCost: 0,
            }
        },

        created() {

            this.settings.milesPerYear = this.defaultMilesPerYear;
            this.settings.totalMonths = this.monthsToSpan;

            this.buildDatasets();
            this.setLabels();
        },

        mounted () {
            this.buildChart();
        },

        methods: {

            buildDatasets() {
                const vehicles = this.evGraphVehicles;
                let graphDatasets = [];

                vehicles.forEach((vehicle, index) => {

                    if (vehicle) {
                        let dataSet = this.vehicleDataObject(vehicle, index);
                        dataSet.data = this.buildPricesArray(vehicle);

                        if (dataSet.isElectric) {
                            this.electricCost = dataSet.data[dataSet.data.length - 1].y;
                            this.totalYears = dataSet.data.length - 1;
                        } else {
                            this.fossilCost = dataSet.data[dataSet.data.length - 1].y;
                        }

                        graphDatasets.push(dataSet);
                    }

                    if (this.fossilCost > this.electricCost) {
                        this.totalSavings = this.fossilCost - this.electricCost;
                    } else {
                        this.totalSavings = 0;
                    }
                });

                this.chartData.datasets = graphDatasets;
                this.calculateSavings();
            },

            calculateSavings() {
                let electricVehicleData, fossilVehicleData;

                if (this.chartData.datasets.length > 1) {
                    electricVehicleData = this.chartData.datasets[0]['data'];
                    fossilVehicleData = this.chartData.datasets[1]['data'];

                    for (let index = 0; index < electricVehicleData.length; index++) {
                        if (fossilVehicleData[index]) {
                            this.costSavings[index] = fossilVehicleData[index]['y'] - electricVehicleData[index]['y'];
                        }
                    }
                }
            },

            setLabels() {
                let labels = [];

                for(var year = 0; year < this.chartData.datasets[0].data.length; year++) {
                    labels.push(year);
                }

                this.chartData.labels = labels;
            },

            buildPricesArray(vehicle) {

                const {
                    purchase_price,
                    home_charger_price,
                    fuel_price_per_mile,
                    service_mot_price_per_year,
                    road_tax_price_per_year
                } = vehicle;

                let total = this.settings.includePurchasePrice ? purchase_price : 0,
                    annualTotals = [{
                        x: 0,
                        y: total,
                        isPurchasePrice: true
                    }];

                const fuelPricePerMonth = ( fuel_price_per_mile * ( this.settings.milesPerYear / 12) );

                let totalYears = 1,
                    homeChargerPriceForYear = 0,
                    fuelPriceForYear = 0;

                //Loop through months
                for(let month = 1; month <= this.settings.totalMonths; month++) {

                    //Home charger
                    if(month === 1) {
                        total += home_charger_price;
                        homeChargerPriceForYear = home_charger_price;
                    }

                    //Fuel
                    total += fuelPricePerMonth;
                    fuelPriceForYear += fuelPricePerMonth;

                    //Every 12 months
                    if( (month % 12) == 0 ) {

                        //Service and MOT
                        total += service_mot_price_per_year;

                        //Road Tax
                        total += road_tax_price_per_year;

                        //Add to graph data
                        let totalObj = {
                            x: totalYears,
                            y: total,
                            serviceMOT: service_mot_price_per_year,
                            homeChargerPrice: homeChargerPriceForYear,
                            roadTaxPrice: road_tax_price_per_year,
                            fuelPrice: fuelPriceForYear
                        };

                        annualTotals.push(totalObj);

                        fuelPriceForYear = 0;
                        homeChargerPriceForYear = 0;
                        totalYears++;
                    }

                }

                return annualTotals;
            },

            vehicleDataObject(vehicle, index) {

                let title = vehicle.vehicle_title,
                    fuelType = vehicle.fuel_type.toLowerCase(),
                    color = vehicle.graph_colour,
                    registrationYear = vehicle.registration_year,
                    isElectric = vehicle.is_electric;

                title = title.replace('Bmw', 'BMW');
                title = title.replace('Mini', 'MINI');

                let obj = {
                    label: registrationYear + ' ' + title,
                    fuelType: fuelType,
                    isElectric: isElectric,
                    backgroundColor: 'transparent',
                    borderColor: color,
                    pointBorderColor: color,
                    pointBackgroundColor: color,
                    borderWidth: 2,
                    pointBorderWidth: 5,
                    data: []
                };

                if (isElectric) {
                    this.electricDataset = obj;
                } else {
                    this.fossilDataset = obj;
                }

                return obj;
            },

            updateChart () {

                if(this.settings.milesPerYear < 0) {
                    this.settings.milesPerYear = 0;
                }

                clearTimeout(this.updateTimeout);

                this.updateTimeout = setTimeout(() => {

                    this.buildDatasets();
                    this.setLabels();
                    this.chart.update();

                }, this.updateDelay);

            },

            getOrCreateTooltip(chart, tooltip) {
                const {offsetLeft: positionX, offsetTop: positionY} = chart.canvas;
                let tooltipEl = chart.canvas.parentNode.querySelector('.evSavingsGraph__tooltip');

                if (!tooltipEl) {
                    tooltipEl = document.createElement('div');
                    tooltipEl.classList.add('evSavingsGraph__tooltip', 'evSavingsTooltip', 'evSavingsTooltip--visible');
                    chart.canvas.parentNode.appendChild(tooltipEl);
                }

                if (tooltip.dataPoints[0].dataset.isElectric) {
                    tooltipEl.classList.remove('evSavingsTooltip--fossil');
                    tooltipEl.classList.add('evSavingsTooltip--electric');
                } else {
                    tooltipEl.classList.remove('evSavingsTooltip--electric');
                    tooltipEl.classList.add('evSavingsTooltip--fossil');
                }

                tooltipEl.style.left = positionX + tooltip.caretX + 'px';
                tooltipEl.style.top = positionY + tooltip.caretY + 'px';

                if (tooltip.opacity === 0) {
                    tooltipEl.classList.remove('evSavingsTooltip--visible');
                } else {
                    tooltipEl.classList.add('evSavingsTooltip--visible');
                }

                return tooltipEl;
            },

            externalTooltipHandler(context) {
                const {chart, tooltip} = context;
                const tooltipEl = this.getOrCreateTooltip(chart, tooltip);

                if (tooltip.opacity === 0) {
                    return;
                }

                const year = tooltip.dataPoints[0].dataIndex;

                tooltipEl.replaceChildren();

                const title = document.createElement('div');
                title.classList.add('evSavingsTooltip__title');
                title.textContent = 'Year ' + tooltip.title;
                tooltipEl.appendChild(title);

                chart.data.datasets.forEach(dataset => {
                    const series = document.createElement('div');
                    series.classList.add('evSavingsTooltip__series');

                    const vehicle = document.createElement('div');
                    const make = document.createElement('div');
                    const fuelType = document.createElement('div');

                    vehicle.classList.add('evSavingsTooltip__vehicle');
                    make.classList.add('evSavingsTooltip__make');
                    make.textContent = dataset.label;
                    fuelType.classList.add('evSavingsTooltip__fuelType');

                    if (dataset.isElectric) {
                        fuelType.classList.add('evSavingsTooltip__fuelType--electric');
                    } else {
                        fuelType.classList.add('evSavingsTooltip__fuelType--fossil');
                    }

                    fuelType.textContent = dataset.fuelType;

                    vehicle.appendChild(make);
                    vehicle.appendChild(fuelType);

                    const cost = document.createElement('div');
                    cost.classList.add('evSavingsTooltip__cost');
                    cost.innerHTML = '<strong>Total cost</strong> ' + this.formatMoney(dataset.data[year].y);

                    series.appendChild(vehicle);
                    series.appendChild(cost);

                    tooltipEl.appendChild(series);
                });

                let electric, fossil, savings, savings_amount = 0;

                if (chart.data.datasets.length > 1) {
                    electric = chart.data.datasets.find(dataset => dataset.isElectric);
                    fossil = chart.data.datasets.find(dataset => !dataset.isElectric);
                }

                if (electric && fossil) {
                    savings_amount = fossil.data[year].y - electric.data[year].y;
                }

                if (savings_amount > 0) {
                    savings = document.createElement('div');
                    savings.classList.add('evSavingsTooltip__cost', 'evSavingsTooltip__cost--savings');
                    savings.innerHTML = '<strong>Total savings</strong> ' + this.formatMoney(savings_amount);
                    tooltipEl.appendChild(savings);
                }
            },

            buildChart () {

                const vm = this;

                this.ctx = document.getElementById('evChart').getContext('2d');

                let aspectRatio = 2;

                if(vm.windowWidth() < 640) {
                    aspectRatio = 1.2;
                }


                this.chart = new Chart(this.ctx, {
                    // The type of chart we want to create
                    type: 'line',

                    // The data for our dataset
                    data: this.chartData,

                    plugins: [
                        {
                            beforeDatasetsDraw: function (chart) {
                                // We get all the variables we need (canvas context and scales)
                                var ctx = chart.ctx;
                                var xAxe = chart.config.options.scales.x;
                                var xScale = chart.scales[xAxe.id];
                                var yAxe = chart.config.options.scales.y;
                                var yScale = chart.scales[yAxe.id];

                                //Zero line color
                                ctx.strokeStyle = '#b7b7b7';

                                //Draw vertical zero line
                                ctx.beginPath();
                                ctx.moveTo(xScale.left, yScale.bottom + 8);
                                ctx.lineTo(xScale.left, yScale.top);
                                ctx.stroke();

                                //Draw horizontal zero line
                                ctx.beginPath();
                                ctx.moveTo(xScale.left - 8, yScale.bottom);
                                ctx.lineTo(xScale.right, yScale.bottom);
                                ctx.stroke();
                            },
                        }
                    ],

                    // Configuration options go here
                    options: {
                        animation: false,
                        plugins: {
                            legend: {
                                display: false,
                            },
                            tooltip: {
                                enabled: false,
                                position: 'nearest',
                                external: vm.externalTooltipHandler,
                            },
                        },
                        responsive: true,
                        maintainAspectRatio: true,
                        aspectRatio: aspectRatio,
                        scales: {
                            y: {
                                ticks: {
                                    beginAtZero: false,
                                    callback: function (value, index, values) {
                                        if (parseInt(value) >= 1000) {
                                            return '£' + value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                                        } else {
                                            return '£' + value;
                                        }
                                    },
                                    padding: 4,
                                    fontColor: '#034e7d',
                                    fontWeight: 'bold'
                                },
                                title: {
                                    display:true,
                                    text:"Cost in £",
                                    color:"#000000",
                                },
                            },
                            x: {
                                ticks: {
                                    padding: 4,
                                    fontColor: '#034e7d',
                                    fontWeight: 'bold'
                                },
                                gridLines: {
                                    borderDash: [3, 2]
                                },
                                title: {
                                    display:true,
                                    text:"Year",
                                    color:"#000000",
                                },
                            }
                        },
                        layout: {
                            padding: {
                                left: 0,
                                right: 0,
                                top: 0,
                                bottom: 0
                            }
                        },
                        legend: {
                            display: false
                        },
                        title: {
                            display: false
                        },
                        defaultFontColor: '#000',
                        defaultFontFamily: 'Helvetica',
                        defaultFontStyle: 'bold',
                    }
                });


            },

            windowWidth() {
                const width  = window.innerWidth || document.documentElement.clientWidth ||
                    document.body.clientWidth;

                return width;
            },

            formatMoney(value) {
                value = parseFloat(value);
                value = value.toFixed(0);
                return '£' + value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            },
        },

        computed: {

            hasTotalSavings() {
                return this.totalSavings > 0;
            },

            fossilCostFormatted() {
                return this.formatMoney(this.fossilCost);
            },

            electricCostFormatted() {
                return this.formatMoney(this.electricCost);
            },

            totalSavingsFormatted() {
                return this.formatMoney(this.totalSavings);
            },
        },
    }

</script>
